import { useState, useMemo, useCallback, memo, useEffect } from 'react';
import { Table, Button, Form, InputGroup, Dropdown, Modal } from 'react-bootstrap';
import IconifyIcon from '../IconifyIcon';
import '../styles/datatable.scss';

// Helper function outside component to prevent recreation
const getNestedValue = (obj, path) => {
  if (!obj || !path) return null;
  return path.split('.').reduce((current, prop) => current?.[prop], obj);
};

const DataTable = memo(({
  data = [],
  columns = [],
  loading = false,
  onEdit,
  onDelete,
  onView,
  onAdd,
  onBulkAction,
  searchable = true,
  pagination = true,
  pageSize: initialPageSize = 10,
  relations = {},
  actions = true,
  selectable = true,
  title = '',
  addButtonText = 'Add New',
  addButtonLabel,
  searchPlaceholder = 'Search...',
  pageSizeOptions = [10, 25, 50, 100],
  onAddClick
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [isMobileView, setIsMobileView] = useState(window.innerWidth <= 768);
  const [imageModal, setImageModal] = useState({ show: false, src: '', alt: '' });

  // Detect mobile view
  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Filter data - simplified
  const filteredData = useMemo(() => {
    if (!data || data.length === 0) return [];
    if (!searchTerm) return data;

    const searchLower = searchTerm.toLowerCase();
    return data.filter((row) => {
      return columns.some((col) => {
        const value = getNestedValue(row, col.accessor);
        if (value === null || value === undefined) return false;
        
        // Handle relations
        if (relations[col.accessor]) {
          const relation = relations[col.accessor];
          const relationData = row[relation.table] || row[relation.accessor];
          if (relationData) {
            const displayValue = typeof relationData === 'object' 
              ? relationData[relation.displayField] 
              : relationData;
            return String(displayValue).toLowerCase().includes(searchLower);
          }
        }
        
        return String(value).toLowerCase().includes(searchLower);
      });
    });
  }, [data, searchTerm, columns, relations]);

  // Sort data - simplified
  const sortedData = useMemo(() => {
    if (!filteredData || filteredData.length === 0) return filteredData;
    if (!sortConfig.key) return filteredData;

    return [...filteredData].sort((a, b) => {
      const aValue = getNestedValue(a, sortConfig.key);
      const bValue = getNestedValue(b, sortConfig.key);

      // Handle relations
      if (relations[sortConfig.key]) {
        const relation = relations[sortConfig.key];
        const aRelation = a[relation.table] || a[relation.accessor];
        const bRelation = b[relation.table] || b[relation.accessor];
        const aDisplay = typeof aRelation === 'object' ? aRelation[relation.displayField] : aRelation;
        const bDisplay = typeof bRelation === 'object' ? bRelation[relation.displayField] : bRelation;
        
        if (aDisplay < bDisplay) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aDisplay > bDisplay) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      }

      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredData, sortConfig, relations]);

  // Paginate data
  const paginatedData = useMemo(() => {
    if (!pagination) return sortedData;
    const startIndex = (currentPage - 1) * pageSize;
    return sortedData.slice(startIndex, startIndex + pageSize);
  }, [sortedData, currentPage, pageSize, pagination]);

  const totalPages = Math.ceil(sortedData.length / pageSize);

  const handleSort = useCallback((key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  }, []);

  const handleSearchChange = useCallback((e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  }, []);

  const handleSelectAll = useCallback((e) => {
    if (e.target.checked) {
      setSelectedRows(new Set(paginatedData.map(row => row.id || row._id)));
    } else {
      setSelectedRows(new Set());
    }
  }, [paginatedData]);

  const handleSelectRow = useCallback((rowId) => {
    setSelectedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(rowId)) {
        newSet.delete(rowId);
      } else {
        newSet.add(rowId);
      }
      return newSet;
    });
  }, []);

  const handlePageSizeChange = useCallback((newSize) => {
    setPageSize(newSize);
    setCurrentPage(1);
  }, []);

  // Get available actions count - support both old and new format
  const isActionsArray = Array.isArray(actions);
  const actionsCount = isActionsArray ? actions.length : [onView, onEdit, onDelete].filter(Boolean).length;
  const showActionsDropdown = actionsCount > 3;
  const hasActions = isActionsArray ? actions.length > 0 : (actions === true && (onView || onEdit || onDelete));

  const renderCell = useCallback((row, column) => {
    const value = getNestedValue(row, column.accessor);

    // Handle relations
    if (relations[column.accessor]) {
      const relation = relations[column.accessor];
      const relationData = row[relation.table] || row[relation.accessor];
      if (relationData) {
        return typeof relationData === 'object' 
          ? relationData[relation.displayField] 
          : relationData;
      }
      return '-';
    }

    // Handle custom render function
    if (column.render) {
      return column.render(value, row);
    }

    // Handle images
    if (column.type === 'image' && value) {
      const imageSrc = Array.isArray(value) ? value[0] : value;
      return (
        <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
          <img
            src={imageSrc}
            alt={column.accessor}
            className="table-image"
            style={{ cursor: 'pointer' }}
            onClick={() => setImageModal({ show: true, src: imageSrc, alt: column.accessor })}
            onError={(e) => {
              // Use data URI for placeholder instead of external image
              e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNTAiIGhlaWdodD0iNTAiIHZpZXdCb3g9IjAgMCA1MCA1MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjUwIiBoZWlnaHQ9IjUwIiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik0yNSAyMEMzMC41MjI4IDIwIDM1IDI0LjQ3NzIgMzUgMzBDMzUgMzUuNTIyOCAzMC41MjI4IDQwIDI1IDQwQzE5LjQ3NzIgNDAgMTUgMzUuNTIyOCAxNSAzMEMxNSAyNC40NzcyIDE5LjQ3NzIgMjAgMjUgMjBaIiBmaWxsPSIjOEM5Q0FBIi8+CjxwYXRoIGQ9Ik0yNSAzNUMzMC41MjI4IDM1IDM1IDMwLjUyMjggMzUgMjVDMzUgMTkuNDc3MiAzMC41MjI4IDE1IDI1IDE1QzE5LjQ3NzIgMTUgMTUgMTkuNDc3MiAxNSAyNUMxNSAzMC41MjI4IDE5LjQ3NzIgMzUgMjUgMzVaIiBmaWxsPSIjOEM5Q0FBIi8+Cjwvc3ZnPgo=';
              e.target.onerror = null; // Prevent infinite loop
            }}
          />
        </div>
      );
    }

    // Handle status
    if (column.type === 'status') {
      const statusConfig = {
        active: { variant: 'success', label: 'Active' },
        inactive: { variant: 'danger', label: 'Inactive' },
        pending: { variant: 'warning', label: 'Pending' },
        completed: { variant: 'success', label: 'Completed' },
        processing: { variant: 'warning', label: 'Processing' },
      };
      const config = statusConfig[value?.toLowerCase()] || { variant: 'secondary', label: value };
      return (
        <span className={`badge bg-soft-${config.variant} text-${config.variant}`}>
          {config.label}
        </span>
      );
    }

    // Handle boolean
    if (column.type === 'boolean') {
      return value ? (
        <IconifyIcon icon="solar:check-circle-bold-duotone" className="text-success" />
      ) : (
        <IconifyIcon icon="solar:close-circle-bold-duotone" className="text-danger" />
      );
    }

    // Default render
    return value ?? '-';
  }, [relations]);

  return (
    <div className="datatable-wrapper">
      <div className="datatable-header">
        {title && <h4 className="datatable-title">{title}</h4>}
        <div className="datatable-actions">
          {searchable && (
            <InputGroup className="datatable-search">
              <InputGroup.Text>
                <IconifyIcon icon="solar:magnifer-linear" />
              </InputGroup.Text>
              <Form.Control
                type="text"
                placeholder={searchPlaceholder}
                value={searchTerm}
                onChange={handleSearchChange}
              />
            </InputGroup>
          )}
          {(onAdd || onAddClick) && (
            <Button variant="primary" onClick={onAdd || onAddClick} className="datatable-add-btn">
              <IconifyIcon icon="solar:add-circle-bold-duotone" className="me-1" />
              {addButtonLabel || addButtonText}
            </Button>
          )}
          {selectable && selectedRows.size > 0 && onBulkAction && (
            <div className="datatable-bulk-actions">
              <InputGroup className="datatable-bulk-dropdown">
                <Form.Select 
                  className="bulk-action-select"
                  onChange={(e) => {
                    if (e.target.value === 'delete') {
                      onBulkAction(Array.from(selectedRows));
                      e.target.value = ''; // Reset after action
                    }
                  }}
                >
                  <option value="">Actions</option>
                  <option value="delete">Bulk Delete</option>
                </Form.Select>
                <InputGroup.Text className="bulk-action-icon">
                  <IconifyIcon icon="solar:menu-dots-bold-duotone" />
                </InputGroup.Text>
              </InputGroup>
            </div>
          )}
        </div>
      </div>

      {/* Mobile Cards View */}
      {isMobileView ? (
        <div className="mobile-cards-view">
          {loading ? (
            <div className="mobile-loading">
              <IconifyIcon icon="solar:loading-bold-duotone" className="spin" />
              <span>Loading...</span>
            </div>
          ) : paginatedData.length === 0 ? (
            <div className="mobile-empty">
              <IconifyIcon icon="solar:document-text-bold-duotone" />
              <span>No data found</span>
            </div>
          ) : (
            paginatedData.map((row) => {
              const rowId = row.id || row._id;
              const isSelected = selectedRows.has(rowId);
              // Get primary display fields
              const nameCol = columns.find(c => c.accessor === 'name' || c.accessor === 'title');
              const imageCol = columns.find(c => c.type === 'image');
              const statusCol = columns.find(c => c.type === 'status');
              const qteCol = columns.find(c => c.accessor === 'qte' || c.accessor === 'quantity' || c.accessor === 'stock');
              const priceCol = columns.find(c => c.accessor === 'price');
              const discountCol = columns.find(c => c.accessor === 'discount');
              
              // Get image source for popup
              const imageSrc = imageCol && row[imageCol.accessor] 
                ? (Array.isArray(row[imageCol.accessor]) ? row[imageCol.accessor][0] : row[imageCol.accessor])
                : null;
              
              return (
                <div key={rowId} className={`mobile-card ${isSelected ? 'selected' : ''}`}>
                  {selectable && (
                    <div className="card-checkbox">
                      <Form.Check
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => handleSelectRow(rowId)}
                      />
                    </div>
                  )}
                  <div className="card-content">
                    {imageCol && imageSrc && (
                      <div 
                        className="card-image"
                        onClick={() => setImageModal({ show: true, src: imageSrc, alt: nameCol ? row[nameCol.accessor] : 'Item' })}
                        style={{ cursor: 'pointer' }}
                      >
                        <img
                          src={imageSrc}
                          alt={nameCol ? row[nameCol.accessor] : 'Item'}
                          onError={(e) => {
                            e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNTAiIGhlaWdodD0iNTAiIHZpZXdCb3g9IjAgMCA1MCA1MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjUwIiBoZWlnaHQ9IjUwIiBmaWxsPSIjRjNGNEY2Ii8+Cjwvc3ZnPgo=';
                          }}
                        />
                      </div>
                    )}
                    <div className="card-info">
                      {nameCol && (
                        <h5 className="card-title">{row[nameCol.accessor]}</h5>
                      )}
                      <div className="card-details">
                        {/* Show Price */}
                        {priceCol && (
                          <div className="card-detail-item">
                            <span className="detail-label">{priceCol.header}:</span>
                            <span className="detail-value">{renderCell(row, priceCol)}</span>
                          </div>
                        )}
                        {/* Show Quantity */}
                        {qteCol && (
                          <div className="card-detail-item">
                            <span className="detail-label">{qteCol.header}:</span>
                            <span className="detail-value">{renderCell(row, qteCol)}</span>
                          </div>
                        )}
                        {/* Show Discount */}
                        {discountCol && (
                          <div className="card-detail-item">
                            <span className="detail-label">{discountCol.header}:</span>
                            <span className="detail-value">{renderCell(row, discountCol)}</span>
                          </div>
                        )}
                      </div>
                      {statusCol && (
                        <div className="card-status">
                          {renderCell(row, statusCol)}
                        </div>
                      )}
                    </div>
                  </div>
                  {hasActions && (
                    <div className="card-actions">
                      {onView && (
                        <Button variant="link" size="sm" onClick={() => onView(row)} className="action-btn-mobile" title="View">
                          <IconifyIcon icon="solar:eye-bold-duotone" />
                        </Button>
                      )}
                      {onEdit && (
                        <Button variant="link" size="sm" onClick={() => onEdit(row)} className="action-btn-mobile" title="Edit">
                          <IconifyIcon icon="solar:pen-bold-duotone" />
                        </Button>
                      )}
                      {onDelete && (
                        <Button variant="link" size="sm" onClick={() => onDelete(row)} className="action-btn-mobile" title="Delete">
                          <IconifyIcon icon="solar:trash-bin-minimalistic-bold-duotone" />
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      ) : (
      /* Desktop Table View */
      <div className="table-responsive">
        <Table hover className="datatable">
          <thead>
            <tr>
              {selectable && (
                <th className="datatable-select-col" style={{ width: '40px' }}>
                  <Form.Check
                    type="checkbox"
                    checked={selectedRows.size === paginatedData.length && paginatedData.length > 0}
                    onChange={handleSelectAll}
                  />
                </th>
              )}
              {columns.map((column) => (
                <th
                  key={column.accessor}
                  onClick={() => column.sortable !== false && handleSort(column.accessor)}
                  className={[
                    column.sortable !== false ? 'sortable' : '',
                    `datatable-col-${column.accessor}`,
                  ].filter(Boolean).join(' ')}
                >
                  <div className="th-content">
                    {column.header}
                    {column.sortable !== false && sortConfig.key === column.accessor && (
                      <IconifyIcon
                        icon={
                          sortConfig.direction === 'asc'
                            ? 'solar:alt-arrow-up-bold-duotone'
                            : 'solar:alt-arrow-down-bold-duotone'
                        }
                        className="ms-1"
                      />
                    )}
                  </div>
                </th>
              ))}
              {hasActions && (
                <th className="datatable-actions-col">
                  <div className="th-content">Actions</div>
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={columns.length + (selectable ? 1 : 0) + (hasActions ? 1 : 0)} className="text-center py-5">
                  <IconifyIcon icon="solar:loading-bold-duotone" className="spin me-2" />
                  Loading...
                </td>
              </tr>
            ) : paginatedData.length === 0 ? (
              <tr>
                <td colSpan={columns.length + (selectable ? 1 : 0) + (hasActions ? 1 : 0)} className="text-center py-5 text-muted">
                  <IconifyIcon icon="solar:document-text-bold-duotone" className="me-2" />
                  No data found
                </td>
              </tr>
            ) : (
              paginatedData.map((row) => {
                const rowId = row.id || row._id;
                const isSelected = selectedRows.has(rowId);
                return (
                  <tr key={rowId} className={isSelected ? 'row-selected' : ''}>
                    {selectable && (
                      <td className="datatable-select-col">
                        <Form.Check
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleSelectRow(rowId)}
                          style={{ margin: 0 }}
                        />
                      </td>
                    )}
                    {columns.map((column) => (
                      <td key={column.accessor} className={`datatable-col-${column.accessor}`}>
                        {renderCell(row, column)}
                      </td>
                    ))}
                    {hasActions && (
                      <td className="datatable-actions-col">
                        {isActionsArray ? (
                          // New format: actions as array
                          <div className="action-buttons">
                            {actions.map((action, index) => {
                              const variantClass = {
                                info: 'text-info',
                                primary: 'text-primary',
                                warning: 'text-warning',
                                danger: 'text-danger',
                                success: 'text-success',
                              }[action.variant] || 'text-primary';
                              
                              return (
                                <Button
                                  key={index}
                                  variant="link"
                                  size="sm"
                                  onClick={() => action.onClick(row)}
                                  className={variantClass}
                                  title={action.label}
                                >
                                  <IconifyIcon icon={action.icon} />
                                </Button>
                              );
                            })}
                          </div>
                        ) : showActionsDropdown ? (
                          <div style={{ display: 'flex', justifyContent: 'center' }}>
                            <Dropdown>
                              <Dropdown.Toggle variant="link" size="sm" className="action-dropdown-toggle">
                                <IconifyIcon icon="solar:menu-dots-bold-duotone" />
                              </Dropdown.Toggle>
                              <Dropdown.Menu align="end">
                              {onView && (
                                <Dropdown.Item onClick={() => onView(row)}>
                                  <IconifyIcon icon="solar:eye-bold-duotone" className="me-2" />
                                  View
                                </Dropdown.Item>
                              )}
                              {onEdit && (
                                <Dropdown.Item onClick={() => onEdit(row)}>
                                  <IconifyIcon icon="solar:pen-bold-duotone" className="me-2" />
                                  Edit
                                </Dropdown.Item>
                              )}
                              {onDelete && (
                                <Dropdown.Item onClick={() => onDelete(row)} className="text-danger">
                                  <IconifyIcon icon="solar:trash-bin-minimalistic-bold-duotone" className="me-2" />
                                  Delete
                                </Dropdown.Item>
                              )}
                            </Dropdown.Menu>
                          </Dropdown>
                          </div>
                        ) : (
                          <div className="action-buttons">
                            {onView && (
                              <Button
                                variant="link"
                                size="sm"
                                onClick={() => onView(row)}
                                className="text-primary"
                                title="View"
                              >
                                <IconifyIcon icon="solar:eye-bold-duotone" />
                              </Button>
                            )}
                            {onEdit && (
                              <Button
                                variant="link"
                                size="sm"
                                onClick={() => onEdit(row)}
                                className="text-warning"
                                title="Edit"
                              >
                                <IconifyIcon icon="solar:pen-bold-duotone" />
                              </Button>
                            )}
                            {onDelete && (
                              <Button
                                variant="link"
                                size="sm"
                                onClick={() => onDelete(row)}
                                className="text-danger"
                                title="Delete"
                              >
                                <IconifyIcon icon="solar:trash-bin-minimalistic-bold-duotone" />
                              </Button>
                            )}
                          </div>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })
            )}
          </tbody>
        </Table>
      </div>
      )}

      {pagination && (
        <div className="datatable-pagination">
          <div className="pagination-left">
            <div className="pagination-info">
              Showing {(currentPage - 1) * pageSize + 1} to {Math.min(currentPage * pageSize, sortedData.length)} of{' '}
              {sortedData.length} entries
            </div>
            <div className="page-size-selector">
              <Form.Select
                size="sm"
                value={pageSize}
                onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                style={{ width: 'auto', display: 'inline-block' }}
              >
                {pageSizeOptions.map((size) => (
                  <option key={size} value={size}>
                    {size} per page
                  </option>
                ))}
              </Form.Select>
            </div>
          </div>
          {totalPages > 1 && (
            <div className="pagination-controls">
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
              >
                <IconifyIcon icon="solar:alt-arrow-left-bold-duotone" />
              </Button>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                return (
                  <Button
                    key={pageNum}
                    variant={currentPage === pageNum ? 'primary' : 'outline-secondary'}
                    size="sm"
                    onClick={() => setCurrentPage(pageNum)}
                  >
                    {pageNum}
                  </Button>
                );
              })}
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
              >
                <IconifyIcon icon="solar:alt-arrow-right-bold-duotone" />
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Image Preview Modal */}
      <Modal 
        show={imageModal.show} 
        onHide={() => setImageModal({ show: false, src: '', alt: '' })}
        centered
        className="image-preview-modal"
      >
        <Modal.Body className="p-0 text-center">
          <button 
            className="modal-close-btn"
            onClick={() => setImageModal({ show: false, src: '', alt: '' })}
          >
            <IconifyIcon icon="solar:close-circle-bold-duotone" />
          </button>
          <img 
            src={imageModal.src} 
            alt={imageModal.alt}
            className="img-fluid"
            style={{ 
              maxHeight: '80vh', 
              maxWidth: '100%',
              borderRadius: '12px'
            }}
          />
        </Modal.Body>
      </Modal>
    </div>
  );
});

DataTable.displayName = 'DataTable';

export default DataTable;
